import { useLocation, useParams } from "react-router-dom";
import { useProjectPipelineScope } from "rwb/pages/project/contexts/pipeline-scope";

/**
 * Root path of the workbench route subtree in whichever shell it is mounted in.
 *
 * The same routes serve two shells: the standalone workbench app under
 * `/workbenches/:projectId`, and the nextgen project shell, which mounts them
 * under the project it provisioned the workbench for. Any in-workbench link
 * built from a literal `/workbenches/...` therefore ejects the user out of the
 * project shell, so links have to be built from this instead.
 *
 * Derived from the live path rather than a mount-point prop because the ~40
 * components that need it all read the workbench id straight off `useParams`.
 * Pipeline views scoped to a project have no workbench, and use the project's
 * mount path instead.
 */
const useWorkbenchBasePath = (workbenchId?: string) => {
  const { projectId } = useParams();
  const { pathname } = useLocation();
  // Most callers read the workbench off the route; components handed it as a
  // prop pass it in, since the route may not carry it.
  const id = workbenchId ?? projectId;
  const pipelineProject = useProjectPipelineScope();

  if (!id) {
    return pipelineProject ? pipelineProject.appBasePath : "/workbenches";
  }
  // First occurrence, so a later path segment that repeats the workbench id (a
  // file named for it, say) can't extend the base past the actual mount point.
  const segment = `/${id}`;
  const mountedAt = pathname.indexOf(`${segment}/`);
  if (mountedAt !== -1) {
    return pathname.slice(0, mountedAt) + segment;
  }
  return pathname.endsWith(segment) ? pathname : `/workbenches/${id}`;
};

export default useWorkbenchBasePath;
